/* eslint-disable no-console */
import type { ErrorComponentProps } from '@tanstack/react-router'

import { Button, Toaster } from '@massilia/ui'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createRootRouteWithContext, Link, Outlet, useRouterState } from '@tanstack/react-router'
import { AlertCircle, RefreshCw, SearchX } from 'lucide-react'
import { lazy, Suspense, useEffect } from 'react'

import { ChatWidget } from '@/components/chat/chat-widget'
import { isChatWidgetEnabled } from '@/lib/feature-flags'

/**
 * Routes du tunnel `_booking` où le chat reste disponible : celles où l'on
 * cherche encore un vol. Passé le choix de l'offre, on remplit un dossier —
 * le widget n'y a plus rien à proposer et masque le bas de formulaires longs.
 * `/recherche` vit hors du tunnel et n'est donc pas concernée.
 */
const CHAT_ENABLED_BOOKING_ROUTES: ReadonlySet<string> = new Set([
  '/_booking/vols-multi/resultats',
  '/_booking/vols/resultats',
])

const TanStackRouterDevtools =
  process.env.NODE_ENV === 'production'
    ? () => null
    : lazy(() =>
        import('@tanstack/router-devtools').then((res) => ({
          default: res.TanStackRouterDevtools,
        }))
      )

export const Route = createRootRouteWithContext()({
  component: () => (
    <div className="bg-gray-25 flex min-h-screen min-w-screen flex-col items-center">
      <App />
      <Suspense>
        <TanStackRouterDevtools />
      </Suspense>
    </div>
  ),
  errorComponent: RootErrorComponent,
  notFoundComponent: () => (
    <div className="flex flex-1 items-center justify-center">
      <div className="flex max-w-md flex-col items-center gap-6 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
          <SearchX className="h-8 w-8 text-amber-600" />
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-stone-800">Page introuvable</h1>
          <p className="text-stone-600">
            La page que vous recherchez n&apos;existe pas ou a été déplacée.
          </p>
        </div>
        <Link
          to="/recherche"
          className="inline-flex items-center gap-2 rounded-lg bg-[#1D95AD] px-6 py-3 font-medium text-white transition-colors hover:bg-[#1a8599]"
        >
          Retour à l&apos;accueil
        </Link>
      </div>
    </div>
  ),
})

function App() {
  const isChatWidgetVisible = useRouterState({
    select: (state) => {
      const isInBookingFunnel = state.matches.some((match) => match.routeId === '/_booking')
      if (!isInBookingFunnel) {
        return true
      }
      return state.matches.some((match) => CHAT_ENABLED_BOOKING_ROUTES.has(match.routeId))
    },
  })

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnMount: true,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        retry: false,
        staleTime: 5 * 1000 * 60, //5 minutes
      },
    },
  })

  return (
    <QueryClientProvider client={queryClient}>
      <DebugDomErrorProbe />
      <Outlet />
      {isChatWidgetEnabled && isChatWidgetVisible ? <ChatWidget /> : null}
      <Toaster position="top-right" richColors theme="system" />
    </QueryClientProvider>
  )
}

function DebugDomErrorProbe() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const debugEnabled =
      import.meta.env.DEV ||
      params.get('debugDomErrors') === '1' ||
      window.localStorage.getItem('debugDomErrors') === '1'

    if (!debugEnabled) {
      return
    }

    let lastInteraction = 'none'

    const describeNode = (target: EventTarget | null) => {
      if (!(target instanceof Element)) {
        return 'unknown'
      }

      const id = target.id ? `#${target.id}` : ''
      const classes =
        typeof target.className === 'string'
          ? `.${target.className.split(/\s+/).filter(Boolean).join('.')}`
          : ''

      return `${target.tagName.toLowerCase()}${id}${classes}`
    }

    const logDomError = (message: string, detail: unknown) => {
      if (!message.includes('removeChild')) {
        return
      }

      console.groupCollapsed('[massilia][dom-error] removeChild')
      console.error('message', message)
      console.error('path', `${window.location.pathname}${window.location.search}`)
      console.error('activeElement', describeNode(document.activeElement))
      console.error('lastInteraction', lastInteraction)
      console.error('detail', detail)
      console.groupEnd()
    }

    const handlePointerDown = (event: PointerEvent) => {
      lastInteraction = describeNode(event.target)
    }

    const handleError = (event: ErrorEvent) => {
      const message = event.error instanceof Error ? event.error.message : event.message
      logDomError(message, event.error ?? event.message)
    }

    const handleRejection = (event: PromiseRejectionEvent) => {
      const message =
        event.reason instanceof Error
          ? event.reason.message
          : typeof event.reason === 'string'
            ? event.reason
            : ''

      logDomError(message, event.reason)
    }

    document.addEventListener('pointerdown', handlePointerDown, true)
    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleRejection)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true)
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleRejection)
    }
  }, [])

  return null
}

function RootErrorComponent({ error, reset }: ErrorComponentProps) {
  const message = error instanceof Error ? error.message : "Une erreur inattendue s'est produite"

  return (
    <div className="flex flex-1 items-center justify-center px-4 py-12">
      <div className="flex w-full max-w-md flex-col items-center gap-6 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
          <AlertCircle className="h-8 w-8 text-red-600" />
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-stone-800">Une erreur est survenue</h1>
          <p className="text-stone-600">
            Nous n&apos;avons pas pu afficher cette page correctement. Vous pouvez réessayer ou
            revenir à l&apos;accueil.
          </p>
        </div>
        <div className="border-gray-light-200 bg-gray-light-50 w-full rounded-xl border px-5 py-4">
          <p className="text-gray-light-700 text-sm wrap-break-word">{message}</p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Button className="bg-brand-600 hover:bg-brand-700" onClick={reset}>
            <RefreshCw className="h-4 w-4" />
            Réessayer
          </Button>
          <Button asChild variant="outline">
            <Link to="/recherche">Retour à l&apos;accueil</Link>
          </Button>
        </div>
      </div>
    </div>
  )
}
